import { styled } from "@mui/material";
import { keyframes } from "@mui/system";
import { AnimationEvent, CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * DigitFlip — per-character re-entry when a figure changes (transitions.dev "Number pop-in", #02).
 *
 * `value` is split into inline-block character spans. When it changes for the same
 * `trackKey`, every character re-enters from `--digit-distance` away through a
 * `--digit-blur` blur, and the last two trail by 1× / 2× `--digit-stagger`. A falling
 * value drops in from above, a rising one comes up from below.
 *
 * It only compares a token's figure with that same token's last one, never plays on a
 * token's first appearance, and never because a row slot was handed a different token:
 * TableComponent keys rows by index, so a sort or a page change feeds this same instance
 * a new `trackKey` without remounting it. Rows can also remount (skeleton swaps), so the
 * last value seen per token is kept in a module-level store as well as a ref. An entry no
 * mounted instance has shown for FORGET_MS is dropped, so opening a page on a figure that
 * changed while you were elsewhere is a first appearance, not a replay.
 *
 * Motion is translate3d + opacity, plus `filter: blur` — the one deliberate exception to
 * the compositor-only rule, asked for by design and kept to this component. `will-change`
 * is only set while a flip plays and cleared on animationend. Reduced motion: JS never
 * starts a flip and the CSS guard strips the animation.
 */

const DUR_MS = 500;
const STAGGER_MS = 70;
/** Safety net for animationend never firing (e.g. the row is display:none mid-flip). */
const SETTLE_MS = DUR_MS + STAGGER_MS * 2 + 200;
/** How long an unmounted token's last value is remembered for a remount to compare against. */
const FORGET_MS = 10_000;

const digitPopIn = keyframes`
  0% {
    transform: translate3d(0, calc(var(--digit-distance) * var(--digit-dir-y)), 0);
    opacity: 0;
    filter: blur(var(--digit-blur));
  }
  100% {
    transform: translate3d(0, 0, 0);
    opacity: 1;
    filter: blur(0);
  }
`;

type Seen = { value: string; mounted: number; forget?: number };
const lastSeen = new Map<string, Seen>();

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const toNumber = (s: string) => parseFloat(s.replace(/[^0-9.-]/g, ""));

const Group = styled("span")`
  --digit-dur: ${DUR_MS}ms;
  --digit-distance: 8px;
  --digit-stagger: ${STAGGER_MS}ms;
  --digit-blur: 2px;
  --digit-ease: cubic-bezier(0.34, 1.45, 0.64, 1);

  position: relative;
  white-space: pre;
  font-variant-numeric: tabular-nums;

  .t-digit {
    display: inline-block;
  }
  &.is-animating .t-digit {
    animation: ${digitPopIn} var(--digit-dur) var(--digit-ease) both;
    will-change: transform, opacity, filter;
  }
  &.is-animating .t-digit[data-stagger="1"] {
    animation-delay: var(--digit-stagger);
  }
  &.is-animating .t-digit[data-stagger="2"] {
    animation-delay: calc(var(--digit-stagger) * 2);
  }

  @media (prefers-reduced-motion: reduce) {
    &.is-animating .t-digit {
      animation: none;
      will-change: auto;
    }
  }
`;

// The character spans are aria-hidden, and aria-label on a plain span is skipped by some
// screen readers in browse mode, so the full value is also spoken from here.
const SrOnly = styled("span")`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  user-select: none;
`;

export type DigitFlipProps = {
  /** Pre-formatted display string. Every character re-enters on a change. */
  value: string;
  /** Identity of the thing the figure belongs to (a token id). Flips only replay for the same key. */
  trackKey: string | number | null | undefined;
  /** Namespaces the store when one token shows several figures (e.g. amount and percentage). */
  scope?: string;
  className?: string;
};

const DigitFlip = ({ value, trackKey, scope = "", className }: DigitFlipProps) => {
  const key = trackKey === null || trackKey === undefined ? null : `${scope}:${trackKey}`;
  // What this instance last painted; undefined until its first layout effect (a fresh mount).
  const shown = useRef<{ key: string | null; value: string }>();
  const [flip, setFlip] = useState<{ gen: number; dir: 1 | -1; active: boolean }>({
    gen: 0,
    dir: 1,
    active: false,
  });

  // Layout effect so a flip is committed before the new value's first paint.
  useLayoutEffect(() => {
    const before = shown.current;
    shown.current = { key, value };
    if (key === null) {
      return undefined;
    }

    const entry = lastSeen.get(key);
    // Same token as this instance's last paint: compare against that. Fresh mount (a
    // remounted row) or a slot now showing another token (sort / pagination): compare
    // against the store, i.e. that token's own last value — so a reorder alone never
    // flips, but a figure that changed and moved (the refetch re-sorted it) still does.
    const previous = before?.key === key ? before.value : entry?.value;

    window.clearTimeout(entry?.forget);
    lastSeen.set(key, { value, mounted: (entry?.mounted ?? 0) + 1 });

    if (previous !== undefined && previous !== value && !prefersReducedMotion()) {
      const dir = toNumber(value) < toNumber(previous) ? -1 : 1;
      setFlip((f) => ({ gen: f.gen + 1, dir, active: true }));
    } else if (before?.key !== key) {
      // A different token took this slot: drop any flip still playing for the last one.
      setFlip((f) => (f.active ? { ...f, active: false } : f));
    }

    return () => {
      const current = lastSeen.get(key);
      if (!current) {
        return;
      }
      current.mounted -= 1;
      if (current.mounted <= 0) {
        current.forget = window.setTimeout(() => lastSeen.delete(key), FORGET_MS);
      }
    };
  }, [key, value]);

  useEffect(() => {
    if (!flip.active) {
      return undefined;
    }
    const timer = window.setTimeout(() => setFlip((f) => ({ ...f, active: false })), SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [flip.active, flip.gen]);

  // The last character carries the longest delay, so its end is the end of the flip.
  // Dropping .is-animating then clears will-change; the `both` fill already rests on the
  // un-animated state, so nothing moves.
  const onAnimationEnd = (e: AnimationEvent<HTMLSpanElement>) => {
    if ((e.target as HTMLElement).dataset.last !== undefined) {
      setFlip((f) => ({ ...f, active: false }));
    }
  };

  const chars = Array.from(value);
  const n = chars.length;

  return (
    <Group
      className={[flip.active ? "is-animating" : "", className].filter(Boolean).join(" ") || undefined}
      style={{ "--digit-dir-y": flip.dir } as CSSProperties}
      aria-label={value}
      onAnimationEnd={onAnimationEnd}
    >
      <SrOnly>{value}</SrOnly>
      {chars.map((ch, i) => (
        <span
          // A new generation remounts the spans, which is what replays the keyframes.
          key={`${flip.gen}-${i}`}
          className="t-digit"
          aria-hidden="true"
          data-stagger={i === n - 2 ? "1" : i === n - 1 ? "2" : undefined}
          data-last={i === n - 1 ? "" : undefined}
        >
          {ch}
        </span>
      ))}
    </Group>
  );
};

export default DigitFlip;
